import React, { useCallback, useEffect, useState } from 'react';
import { consumerApi } from 'lib/consumer-api';
import { API_ENDPOINTS } from 'constants/endpoints';
import { Lock, Search } from 'lucide-react';
import ConsumerListPageLayout from 'components/consumer/ConsumerListPageLayout';
import { ConsumerFilterField, consumerFilterInputClass, consumerFilterInputMonoClass } from 'components/consumer/ConsumerFilterBar';
import { CONSUMER_TD_CLASS, CONSUMER_TH_CLASS } from 'components/consumer/consumer-list-styles';
import { appendCursorParams, parseCursorPagination } from 'lib/cursor-pagination';
import { useCursorListPage } from 'hooks/useCursorListPage';
import { PRIVATE_TABLE_STATUS_LABEL, labelFor } from 'constants/enums';
import type { ConsumerPortalPrivateTable } from 'types/consumer';

const money = (v: number | null | undefined) => Number(v ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 });

type PrivateTableFilters = { roomCode: string; tableName: string; createdBy: string; status: string };
const emptyFilters: PrivateTableFilters = { roomCode: '', tableName: '', createdBy: '', status: '' };
const countFilters = (f: PrivateTableFilters) =>
  (f.roomCode.trim() ? 1 : 0) + (f.tableName.trim() ? 1 : 0) + (f.createdBy.trim() ? 1 : 0) + (f.status ? 1 : 0);

function buildFilterParams(filters: PrivateTableFilters) {
  const params = new URLSearchParams();
  if (filters.roomCode.trim()) params.set('roomCode', filters.roomCode.trim());
  if (filters.tableName.trim()) params.set('tableName', filters.tableName.trim());
  if (filters.createdBy.trim()) params.set('createdBy', filters.createdBy.trim());
  if (filters.status) params.set('status', filters.status);
  return params;
}

const ConsumerPrivateTables = () => {
  const [draftFilters, setDraftFilters] = useState(emptyFilters);
  const [appliedFilters, setAppliedFilters] = useState(emptyFilters);

  const fetchPage = useCallback(async (cursor: number | null) => {
    const params = appendCursorParams(buildFilterParams(appliedFilters), cursor);
    const res = await consumerApi.get<ConsumerPortalPrivateTable[]>(`${API_ENDPOINTS.CONSUMER_PORTAL.PRIVATE_TABLES}?${params.toString()}`);
    const meta = parseCursorPagination(res);
    return { items: res.success && Array.isArray(res.data) ? res.data : [], nextCursor: meta.nextCursor, hasMore: meta.hasMore, total: meta.total, totalPages: meta.totalPages };
  }, [appliedFilters]);

  const { page, items, loading, refreshing, total, totalPages, limit, resetAndLoad, refreshCurrent, goFirst, goToPage, goLast } = useCursorListPage(fetchPage);
  useEffect(() => { resetAndLoad(); }, [appliedFilters, resetAndLoad]);

  return (
    <ConsumerListPageLayout
      icon={Lock} title="Private Tables" description="Private tables for your consumer only (read-only)."
      totalCount={null} filteredCount={items.length}
      loading={loading} refreshing={refreshing} onRefresh={() => refreshCurrent()}
      activeFilterCount={countFilters(appliedFilters)} draftFilterCount={countFilters(draftFilters)}
      onApplyFilters={() => setAppliedFilters({ ...draftFilters })}
      onClearFilters={() => { setDraftFilters(emptyFilters); setAppliedFilters(emptyFilters); }}
      filterFields={
        <>
          <ConsumerFilterField label="Room code">
            <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" size={15} />
              <input type="text" placeholder="Search room code" value={draftFilters.roomCode} onChange={(e) => setDraftFilters((p) => ({ ...p, roomCode: e.target.value }))} className={`${consumerFilterInputMonoClass} pl-9`} />
            </div>
          </ConsumerFilterField>
          <ConsumerFilterField label="Table name"><input type="text" value={draftFilters.tableName} onChange={(e) => setDraftFilters((p) => ({ ...p, tableName: e.target.value }))} className={consumerFilterInputClass} /></ConsumerFilterField>
          <ConsumerFilterField label="Created by"><input type="text" value={draftFilters.createdBy} onChange={(e) => setDraftFilters((p) => ({ ...p, createdBy: e.target.value }))} className={consumerFilterInputClass} /></ConsumerFilterField>
          <ConsumerFilterField label="Status"><input type="text" placeholder="Status code" value={draftFilters.status} onChange={(e) => setDraftFilters((p) => ({ ...p, status: e.target.value }))} className={consumerFilterInputMonoClass} /></ConsumerFilterField>
        </>
      }
      tableTitle="Private Tables" tableSubtitle="Invite-only tables created by your players"
      emptyIcon={Lock} emptyTitle="No private tables" emptyDescription="Private tables will appear here when players create them."
      isDatasetEmpty={total === 0 && !loading && !countFilters(appliedFilters)}
      isFilteredEmpty={items.length === 0 && !loading && countFilters(appliedFilters) > 0}
      colSpan={7} page={page} itemCount={items.length} limit={limit} total={total} totalPages={totalPages} onFirst={goFirst} onPageChange={goToPage} onLast={goLast}
      tableHead={<tr><th className={CONSUMER_TH_CLASS}>Room Code</th><th className={CONSUMER_TH_CLASS}>Name</th><th className={CONSUMER_TH_CLASS}>Variant</th><th className={CONSUMER_TH_CLASS}>Boot</th><th className={CONSUMER_TH_CLASS}>Players</th><th className={CONSUMER_TH_CLASS}>Created By</th><th className={CONSUMER_TH_CLASS}>Status</th></tr>}
      tableBody={<>{items.map((t) => (
        <tr key={t.id} className="hover:bg-subtle/40 transition-colors">
          <td className={`${CONSUMER_TD_CLASS} whitespace-nowrap`}><span className="font-mono text-sm font-semibold text-brand-gold">{t.roomCode}</span></td>
          <td className={`${CONSUMER_TD_CLASS} whitespace-nowrap`}><span className="text-sm text-body">{t.tableName || '—'}</span></td>
          <td className={`${CONSUMER_TD_CLASS} whitespace-nowrap`}><span className="text-sm text-body">{t.variantName || t.variantCode || '—'}</span></td>
          <td className={`${CONSUMER_TD_CLASS} whitespace-nowrap`}><span className="text-sm font-semibold text-heading">{t.currencySymbol || '₹'}{money(t.bootAmount)}</span></td>
          <td className={`${CONSUMER_TD_CLASS} whitespace-nowrap`}><span className="text-sm text-body">{t.currentPlayers}/{t.maxSeats}</span></td>
          <td className={`${CONSUMER_TD_CLASS} whitespace-nowrap`}><span className="text-sm text-body">{t.createdByName || '—'}</span></td>
          <td className={`${CONSUMER_TD_CLASS} whitespace-nowrap`}><span className="px-2 py-0.5 text-[10px] font-bold rounded-md uppercase bg-subtle text-muted border border-line">{labelFor(PRIVATE_TABLE_STATUS_LABEL, t.status)}</span></td>
        </tr>
      ))}</>}
    />
  );
};

export default ConsumerPrivateTables;
